import React, { useRef, useState, useEffect } from 'react';
import { Button } from '../../components/ui/Button';
import Spinner from '../../components/ui/Spinner';
import DateRangePicker from '../../components/shared/filters/DateRangePicker';
import { PageHeader } from '../../components/layout/Header';
import { usePermissions } from '../../hooks/usePermissions';
import DashboardStats from './components/DashboardStats';
import LeadsByPeriodChart from './components/LeadsByPeriodChart';
import LeadsByStatusChart from './components/LeadsByStatusChart';
import SalesByMonthChart from './components/SalesByMonthChart';
import SalesByChannelChart from './components/SalesByChannelChart';
import ConversationsByChannelChart from './components/ConversationsByChannelChart';
import SalesRankTable from './components/SalesRankTable';
import LtvRankTable from './components/LtvRankTable';
import LostReasonsChart from './components/LostReasonsChart';
import AnalysisModal from './components/AnalysisModal';
import { useDashboard } from './hooks/useDashboard';

const SkeletonBlock = ({ className }: { className: string }) => (
    <div className={`animate-pulse rounded-xl bg-surface-subtle ${className}`} />
);

const Dashboard: React.FC = () => {
    const { canViewReports } = usePermissions();

    const {
        leadsByDay,
        leadsByStatus,
        salesByMonth,
        salesByChannel,
        isAnalysisModalOpen,
        analysisResult,
        analysisError,
        isLoadingAnalysis,
        handleAnalyze,
        closeAnalysisModal,
        stats,
        dateRange,
        setDateRange,
        salesRanking,
        ltvRanking,
        conversationsByChannel,
        isLoadingChannels,
        leadsByLostReason,
        isLoading,
    } = useDashboard();

    const hasInitiallyLoaded = useRef(!isLoading);
    const [showLoader, setShowLoader] = useState(isLoading);
    const [isFading, setIsFading] = useState(false);

    useEffect(() => {
        if (!isLoading) {
            if (showLoader) {
                setIsFading(true);
                const timer = setTimeout(() => {
                    setShowLoader(false);
                }, 200);
                return () => clearTimeout(timer);
            }
            hasInitiallyLoaded.current = true;
        } else if (!hasInitiallyLoaded.current) {
            setShowLoader(true);
            setIsFading(false);
        }
    }, [isLoading, showLoader]);

    if (showLoader && !hasInitiallyLoaded.current) {
        return (
            <div
                className={`flex flex-col gap-s4 transition-opacity duration-200 ease-in-out md:gap-s5 ${isFading ? 'opacity-0' : 'opacity-100'}`}
            >
                <div className="flex justify-end">
                    <SkeletonBlock className="h-11 w-48 rounded-md" />
                </div>
                <div className="grid grid-cols-1 gap-s3 md:grid-cols-4 md:gap-s4">
                    {[...Array(4)].map((_, i) => (
                        <article key={i} className="kpi-card">
                            <div className="kpi-accent" aria-hidden />
                            <div className="mb-s2 flex items-center justify-between">
                                <SkeletonBlock className="h-3 w-16" />
                                <SkeletonBlock className="size-8 rounded-lg" />
                            </div>
                            <SkeletonBlock className="mb-s2 h-7 w-12" />
                            <SkeletonBlock className="h-4 w-24" />
                        </article>
                    ))}
                </div>
                <div className="grid grid-cols-1 gap-s4 lg:grid-cols-2">
                    {[...Array(2)].map((_, i) => (
                        <div key={i} className="card p-0">
                            <div className="border-b border-subtle px-s4 py-s3">
                                <SkeletonBlock className="h-4 w-32" />
                            </div>
                            <div className="p-s4">
                                <SkeletonBlock className="h-52 md:h-56" />
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <main className="flex flex-col gap-s4 md:gap-s5">
            <header>
                <PageHeader
                    title="Dashboard"
                    subtitle="Visão geral do seu negócio."
                    className="mb-0 gap-s3"
                    titleClassName="page-header-title md:text-xl"
                    subtitleClassName="page-header-subtitle"
                    actionClassName="w-full md:w-auto"
                    hideBackButton
                    action={
                        <>
                            <div className="flex w-full flex-col gap-s3 md:hidden">
                                <DateRangePicker value={dateRange} onChange={setDateRange} />
                                {canViewReports ? (
                                    <Button
                                        type="button"
                                        onClick={handleAnalyze}
                                        disabled={isLoadingAnalysis}
                                        className="min-h-11 w-full touch-manipulation"
                                    >
                                        {isLoadingAnalysis ? <Spinner /> : 'Executar Análise com IA'}
                                    </Button>
                                ) : null}
                            </div>
                            <div className="hidden md:flex md:flex-wrap md:items-center md:gap-s2">
                                <DateRangePicker value={dateRange} onChange={setDateRange} />
                                {canViewReports ? (
                                    <Button
                                        type="button"
                                        onClick={handleAnalyze}
                                        disabled={isLoadingAnalysis}
                                        variant="default"
                                    >
                                        {isLoadingAnalysis ? <Spinner /> : 'Executar Análise com IA'}
                                    </Button>
                                ) : null}
                            </div>
                        </>
                    }
                />
            </header>

            <DashboardStats stats={stats} />

            <section aria-label="Negociações e vendas" className="grid grid-cols-1 gap-s4 lg:grid-cols-2">
                <LeadsByPeriodChart data={leadsByDay} />
                {canViewReports ? (
                    <SalesByMonthChart data={salesByMonth} />
                ) : (
                    <LeadsByStatusChart data={leadsByStatus} />
                )}
            </section>

            <section aria-label="Distribuição de leads" className="grid grid-cols-1 gap-s4 lg:grid-cols-3">
                {canViewReports ? <LeadsByStatusChart data={leadsByStatus} /> : null}
                <SalesByChannelChart data={salesByChannel} />
                <ConversationsByChannelChart
                    data={conversationsByChannel}
                    isLoading={isLoadingChannels}
                />
                {!canViewReports ? <LostReasonsChart data={leadsByLostReason} /> : null}
            </section>

            {canViewReports ? (
                <section aria-label="Rankings e perdas" className="grid grid-cols-1 gap-s4 lg:grid-cols-3">
                    <SalesRankTable ranking={salesRanking} />
                    <LtvRankTable ranking={ltvRanking} />
                    <LostReasonsChart data={leadsByLostReason} />
                </section>
            ) : null}

            <AnalysisModal
                isOpen={isAnalysisModalOpen}
                onClose={closeAnalysisModal}
                isLoading={isLoadingAnalysis}
                analysisResult={analysisResult}
                analysisError={analysisError}
                onRetry={handleAnalyze}
            />
        </main>
    );
};

export default Dashboard;
